import { useTranslation } from 'react-i18next';
import { useStore } from '../store';

// Top-centre toolbar shown only while an edit mode is active (draw field / edit outline / add zone).
export function EditorBar() {
  const { t } = useTranslation();
  const editMode = useStore((s) => s.editMode);
  const draftLen = useStore((s) => s.draftRing.length);
  const zones = useStore((s) => s.zones);
  const selectedZoneIndex = useStore((s) => s.selectedZoneIndex);
  if (editMode === 'none') return null;

  const s = useStore.getState();
  // Resize/delete target: the selected zone, else the most recent one.
  const targetIdx = selectedZoneIndex ?? (zones.length > 0 ? zones.length - 1 : -1);
  const hasTarget = targetIdx >= 0;
  const targetRadius = hasTarget ? zones[targetIdx]!.radiusM : 10;

  const Btn = ({
    onClick,
    children,
    kind = 'plain',
    disabled,
  }: {
    onClick: () => void;
    children: string;
    kind?: 'plain' | 'go' | 'danger';
    disabled?: boolean;
  }) => (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`rounded-xl px-4 py-2 text-sm font-semibold disabled:opacity-40 ${
        kind === 'go'
          ? 'bg-emerald-500 text-emerald-950'
          : kind === 'danger'
            ? 'bg-red-600 text-white'
            : 'bg-zinc-700 text-white'
      }`}
    >
      {children}
    </button>
  );

  return (
    <div className="pointer-events-auto absolute left-1/2 top-3 flex max-w-[92vw] -translate-x-1/2 flex-col items-center gap-2 rounded-2xl bg-zinc-900/90 px-4 py-3 text-white shadow-2xl backdrop-blur">
      {editMode === 'drawField' && (
        <>
          <span className="text-sm text-white/70">{t('editor.tapToAdd')}</span>
          <div className="flex gap-2">
            <Btn onClick={() => s.undoDraft()} disabled={draftLen === 0}>
              {t('editor.undo')}
            </Btn>
            <Btn onClick={() => s.cancelEdit()}>{t('editor.cancel')}</Btn>
            <Btn onClick={() => s.finishDrawField()} kind="go" disabled={draftLen < 3}>
              {t('editor.finish')}
            </Btn>
          </div>
        </>
      )}

      {editMode === 'editField' && (
        <div className="flex items-center gap-2">
          <span className="text-sm text-white/70">{t('editor.editFieldHint')}</span>
          <Btn onClick={() => s.finishEdit()} kind="go">
            {t('editor.done')}
          </Btn>
        </div>
      )}

      {editMode === 'addZone' && (
        <>
          <span className="text-sm text-white/70">{t('editor.zoneHint')}</span>
          <div className="flex items-center gap-2">
            <Btn onClick={() => s.setZoneRadius(targetIdx, targetRadius - 2)} disabled={!hasTarget}>
              −
            </Btn>
            <span className="w-16 text-center text-sm tabular-nums">
              {targetRadius} {t('units.m')}
            </span>
            <Btn onClick={() => s.setZoneRadius(targetIdx, targetRadius + 2)} disabled={!hasTarget}>
              +
            </Btn>
            <Btn onClick={() => s.removeZone(targetIdx)} kind="danger" disabled={!hasTarget}>
              {t('editor.deleteZone')}
            </Btn>
            <Btn onClick={() => s.clearZones()} disabled={zones.length === 0}>
              {t('editor.clearZones')}
            </Btn>
            <Btn onClick={() => s.finishEdit()} kind="go">
              {t('editor.done')}
            </Btn>
          </div>
        </>
      )}
    </div>
  );
}
